import { ReactNode, useState } from "react";

import { UnifiedRegistryProvider } from "../UnifiedRegistry/unified-registry-context";

import ChooseEntityDialogContent from "./ChooseEntityDialogContent";
import { isSameEntityVariant } from "./ChooseEntityDialogContent/utils";
import ChooseEntityDialogTrigger from "./ChooseEntityDialogTrigger";
import { ChooseEntityConfig, ChooseEntityConfigProvider } from "./choose-entity-config-context";
import {
  ChooseEntityDialogProps,
  isMultiSelectProps,
  MultiSelectProps,
  SingleSelectProps,
} from "./types";

import { Entity, EntityType } from "@/queries";

// Re-export types for external use
export { isMultiSelectProps, isSingleSelectProps } from "./types";
export type { ChooseEntityDialogProps, MultiSelectProps, SingleSelectProps } from "./types";

export interface EntityActionProps {
  onInlineCreate?: () => void;
  onInlineEdit?: () => void;
  url?: string;
  readonly?: boolean;
  isLoading?: boolean;
}

export const useChooseEntityDialog = () => {
  const [open, setOpen] = useState(false);

  const openDialog = () => setOpen(true);
  const closeDialog = () => setOpen(false);

  return {
    open,
    openDialog,
    closeDialog,
    setOpen,
  };
};

export const ChooseEntityDialogProvider = <T extends EntityType = EntityType>({
  children,
  type,
  selectedEntity,
  config,
  disabled,
  mode = "single",
}: {
  children: ReactNode;
  type: T;
  selectedEntity?: Entity;
  selectedEntities?: Entity[];
  config?: Partial<ChooseEntityConfig>;
  disabled?: Entity[] | ((entity: Entity<T>) => boolean);
  mode?: "single" | "multi";
}) => {
  const allowDuplicateVariants = config?.allowDuplicateVariants ?? false;

  const getDisabledLogic = () => {
    const getDefaultDisabled = (): ((entity: Entity) => boolean) => (entity: Entity) => {
      if (mode === "single" && selectedEntity && config?.selectLatest) {
        if (isSameEntityVariant(entity, selectedEntity, allowDuplicateVariants)) {
          return true;
        }
      }

      return false;
    };

    const defaultDisabledFn = getDefaultDisabled();

    if (!disabled) {
      return defaultDisabledFn;
    }

    const customDisabledFn: (entity: Entity) => boolean =
      typeof disabled === "function"
        ? (disabled as (entity: Entity) => boolean)
        : (entity: Entity) =>
            disabled.some(disabledEntity =>
              isSameEntityVariant(entity, disabledEntity, allowDuplicateVariants),
            );

    return (entity: Entity) => defaultDisabledFn(entity) || customDisabledFn(entity);
  };

  return (
    <ChooseEntityConfigProvider
      config={{
        types: type === EntityType.PROMPT ? [type, EntityType.SNIPPET] : [type],
        mode,
        ...config,
      }}
    >
      <UnifiedRegistryProvider
        options={{
          whitelist: type === EntityType.PROMPT ? [type, EntityType.SNIPPET] : [type],
          disableSelection: mode === "single",
          createInNewTab: true,
          disabled: getDisabledLogic(),
          modal: true,
          multiSelectMode: mode === "multi",
          allowDuplicateVariants: config?.allowDuplicateVariants ?? false,
        }}
      >
        {children}
      </UnifiedRegistryProvider>
    </ChooseEntityConfigProvider>
  );
};

export const ChooseEntityDialogWithCustomTrigger = <T extends EntityType = EntityType>({
  children: trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  ...props
}: ChooseEntityDialogProps<T> & {
  children: ReactNode;
}) => {
  const { open: internalOpen, setOpen: setInternalOpen } = useChooseEntityDialog();

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const onOpenChange = controlledOnOpenChange || setInternalOpen;

  if (isMultiSelectProps(props)) {
    const multiProps = props as MultiSelectProps<T>;
    return (
      <ChooseEntityDialogProvider
        type={multiProps.type}
        selectedEntities={multiProps.selectedEntities}
        config={multiProps.config}
        disabled={multiProps.disabled}
        mode="multi"
      >
        <ChooseEntityDialogContent
          open={open}
          onOpenChange={onOpenChange}
          mode="multi"
          type={multiProps.type}
          onSelect={multiProps.onSelect as (entities: Entity[]) => void}
          selectedEntities={multiProps.selectedEntities as Entity[] | undefined}
          currentFolderId={multiProps.currentFolderId}
          readonly={multiProps.readonly}
          min={multiProps.min}
          max={multiProps.max}
        >
          {trigger}
        </ChooseEntityDialogContent>
      </ChooseEntityDialogProvider>
    );
  }

  const singleProps = props as SingleSelectProps<T>;
  return (
    <ChooseEntityDialogProvider
      type={singleProps.type}
      selectedEntity={singleProps.selectedEntity}
      config={singleProps.config}
      disabled={singleProps.disabled}
      mode="single"
    >
      <ChooseEntityDialogContent
        open={open}
        onOpenChange={onOpenChange}
        mode="single"
        type={singleProps.type}
        onSelect={singleProps.onSelect as (entity: Entity) => void}
        selectedEntity={singleProps.selectedEntity as Entity | undefined}
        currentFolderId={singleProps.currentFolderId}
        readonly={singleProps.readonly}
      >
        {trigger}
      </ChooseEntityDialogContent>
    </ChooseEntityDialogProvider>
  );
};

export const ChooseEntityDialogControlled = <T extends EntityType = EntityType>({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  ...props
}: ChooseEntityDialogProps<T>) => {
  if (controlledOpen === undefined || !controlledOnOpenChange) {
    throw new Error("ChooseEntityDialogControlled requires both open and onOpenChange props");
  }

  if (isMultiSelectProps(props)) {
    const multiProps = props as MultiSelectProps<T>;
    return (
      <ChooseEntityDialogProvider
        type={multiProps.type}
        selectedEntities={multiProps.selectedEntities}
        config={multiProps.config}
        disabled={multiProps.disabled}
        mode="multi"
      >
        <ChooseEntityDialogContent
          open={controlledOpen}
          onOpenChange={controlledOnOpenChange}
          mode="multi"
          type={multiProps.type}
          onSelect={multiProps.onSelect as (entities: Entity[]) => void}
          selectedEntities={multiProps.selectedEntities as Entity[] | undefined}
          currentFolderId={multiProps.currentFolderId}
          readonly={multiProps.readonly}
          min={multiProps.min}
          max={multiProps.max}
        />
      </ChooseEntityDialogProvider>
    );
  }

  const singleProps = props as SingleSelectProps<T>;
  return (
    <ChooseEntityDialogProvider
      type={singleProps.type}
      selectedEntity={singleProps.selectedEntity}
      config={singleProps.config}
      disabled={singleProps.disabled}
      mode="single"
    >
      <ChooseEntityDialogContent
        open={controlledOpen}
        onOpenChange={controlledOnOpenChange}
        mode="single"
        type={singleProps.type}
        onSelect={singleProps.onSelect as (entity: Entity) => void}
        selectedEntity={singleProps.selectedEntity as Entity | undefined}
        currentFolderId={singleProps.currentFolderId}
        readonly={singleProps.readonly}
      />
    </ChooseEntityDialogProvider>
  );
};

const ChooseEntityDialog = <T extends EntityType = EntityType>(
  props: ChooseEntityDialogProps<T> & EntityActionProps,
) => {
  const { open: internalOpen, setOpen: setInternalOpen } = useChooseEntityDialog();

  // eslint-disable-next-line react/destructuring-assignment
  const open = props.open !== undefined ? props.open : internalOpen;
  // eslint-disable-next-line react/destructuring-assignment
  const onOpenChange = props.onOpenChange || setInternalOpen;

  if (isMultiSelectProps(props)) {
    const multiProps = props as MultiSelectProps<T> & EntityActionProps;
    return (
      <ChooseEntityDialogProvider
        type={multiProps.type}
        selectedEntities={multiProps.selectedEntities}
        config={multiProps.config}
        disabled={multiProps.disabled}
        mode="multi"
      >
        <ChooseEntityDialogContent
          open={open}
          onOpenChange={onOpenChange}
          mode="multi"
          type={multiProps.type}
          onSelect={multiProps.onSelect as (entities: Entity[]) => void}
          selectedEntities={multiProps.selectedEntities as Entity[] | undefined}
          currentFolderId={multiProps.currentFolderId}
          readonly={multiProps.readonly}
          min={multiProps.min}
          max={multiProps.max}
        >
          <ChooseEntityDialogTrigger
            type={multiProps.type}
            size={multiProps.size}
            className={multiProps.className}
            mode="multi"
            selectedEntities={multiProps.selectedEntities}
            onSelect={multiProps.onSelect as ((entities: Entity[]) => void) | undefined}
            onInlineCreate={multiProps.readonly ? undefined : multiProps.onInlineCreate}
            readonly={multiProps.readonly}
            isLoading={multiProps.isLoading}
            hasError={multiProps.hasError}
            errorMessage={multiProps.errorMessage}
          />
        </ChooseEntityDialogContent>
      </ChooseEntityDialogProvider>
    );
  }

  const singleProps = props as SingleSelectProps<T> & EntityActionProps;
  return (
    <ChooseEntityDialogProvider
      type={singleProps.type}
      selectedEntity={singleProps.selectedEntity}
      config={singleProps.config}
      disabled={singleProps.disabled}
      mode="single"
    >
      <ChooseEntityDialogContent
        open={open}
        onOpenChange={onOpenChange}
        mode="single"
        type={singleProps.type}
        onSelect={singleProps.onSelect as (entity: Entity) => void}
        selectedEntity={singleProps.selectedEntity as Entity | undefined}
        currentFolderId={singleProps.currentFolderId}
        readonly={singleProps.readonly}
      >
        <ChooseEntityDialogTrigger
          type={singleProps.type}
          size={singleProps.size}
          className={singleProps.className}
          mode="single"
          selectedEntity={singleProps.selectedEntity}
          onInlineCreate={singleProps.readonly ? undefined : singleProps.onInlineCreate}
          onInlineEdit={singleProps.readonly ? undefined : singleProps.onInlineEdit}
          url={singleProps.url}
          readonly={singleProps.readonly}
          isLoading={singleProps.isLoading}
          hasError={singleProps.hasError}
          errorMessage={singleProps.errorMessage}
        />
      </ChooseEntityDialogContent>
    </ChooseEntityDialogProvider>
  );
};

export default ChooseEntityDialog;
